import { TenantConfigViewItem, TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { Option } from 'src/components/Configure/ConfigureModal';
import { setActivePage } from 'src/pages/NavigationShell/NavigationShell.slice';
import { receiveWorklistConfig, updateSelectedLevel } from 'src/pages/Worklist/Worklist.slice';
import { receivePricingOverTimeConfig } from 'src/pages/AssortmentBuild/Pricing/PricingOverTime.slice';
import { receiveGridViewConfig } from 'src/pages/Hindsighting/StyleColorReview/GridView/GridView.slice';
import { receiveStyleEditConfigs } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.slice';
import { receiveTenantConfig as receiveStyleByLevelOvertimeConfig } from 'src/pages/AssortmentBuild/OvertimeView/StyleByLevel/StyleByLevelOvertime.slice';
import { parseSortConfig } from 'src/utils/Config/Sort';
import { logWarn } from 'src/services/loggingService';
import { get, cloneDeep, isNil, isNumber } from 'lodash';
import { FlowStatus } from 'configuration-schemas/bindings.types';
import {
  FavoriteResponseItem,
  ValidatedAssortmentFavorite,
  ValidatedFavoriteResponse,
} from 'src/components/Subheader/Favorites/Favorites.types';
import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import ServiceContainer from 'src/ServiceContainer';
import type { ExtraPivotParam, SubheaderDefnProps } from 'configuration-schemas/confdefnComponentProps';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import { BasicItem } from 'src/worker/pivotWorker.types';
import { Option as DropdownOption } from 'src/components/Subheader/Subheader';
import { receiveFlowSheetByStyleConfig } from 'src/pages/AssortmentBuild/FlowSheet/FlowSheetByStyle.slice';
import { receiveConfig as listViewReceiveConfig } from 'src/pages/Hindsighting/HistoryGrid/ListView/ListView.slice';
import { receiveConfig as nestedViewReceiveConfig } from 'src/pages/Hindsighting/HistoryGrid/NestedView/NestedView.slice';
import {
  receiveScope,
  receiveScopeRefreshTrigger,
  resetOnlyScope,
  resetScope,
} from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import type { AttributeBlacklistConfig } from 'configuration-schemas/viewdefns/general';
import { blacklistByDataIndex } from 'src/utils/Component/AttributeBlacklist.utils';
import { floorsetInUrl } from 'src/utils/Functions/epicsFunctions';

export type TopMemberDropdownType = 'Floorset' | 'Superset' | 'Design Season';

export interface SubheaderViewDefns extends SubheaderDefnProps {}

export interface SubheaderDefns {
  groupBy: DropdownConfigViewData;
  sortBy: DropdownConfigViewData;
  countLimit: DropdownConfigViewData;
  pareDown: DropdownConfigViewData;
}

// FIXME: do we need to include extra props here if only manipulating groupByOptions?
export interface OverLoadSubheaderProps {
  title?: 'Grid View' | 'Quick Snapshot';
  showFlowStatus?: boolean;
  showSearch: true;
  groupByOptions?: DropdownConfigViewData;
}

export interface SubheaderDropdownSlice {
  defaultSelection: number;
  selection: number | null;
  selectionDataIndex?: string | null;
  options: TenantConfigViewItem[];
  refreshOnChange?: boolean;
}

export interface DropdownConfigViewData extends TenantConfigViewData {
  hideEmptyRow?: boolean;
  refreshOnChange?: boolean;
}

export interface GroupBySlice extends SubheaderDropdownSlice {}

export interface SortBySlice extends SubheaderDropdownSlice {
  direction: SortByDirection;
}
export interface GroupByPayload {
  options: TenantConfigViewItem[];
  selection: number | null;
}
export interface PareDownSlice {
  defaultSelections: TenantConfigViewItem[];
  selections: TenantConfigViewItem[];
  options: TenantConfigViewItem[];
}

export interface SubheaderSlice {
  title?: string;
  showFlowStatus?: boolean;
  showSearch?: true;
  search: string;
  altSearch: string;
  groupBy: GroupBySlice;
  groupByDefn?: TenantConfigViewData;
  sortBy: SortBySlice;
  sortByDefn?: TenantConfigViewData;
  pareDown: PareDownSlice;
  pareDownDefn?: TenantConfigViewData;
  lookBackPeriod: string;
  countLimit?: number;
  countLimitOptions?: number[];
  countLimitDefault?: number;
  subheaderLoading: boolean;
  configureSelections?: Option[];
  flowStatus: number[];
  flowStatusConfig?: FlowStatus;
  altFlowStatus: number[];
  /** @deprecated use `favorites` instead of `favoritesList` */
  favoritesList?: FavoriteResponseItem[];
  favorites: ValidatedFavoriteResponse[];
  activeFavorite: ValidatedAssortmentFavorite | null;
  possibleBreadcrumbs: string[] | null;
  // topMemberDropdown props
  topMemberOptions?: BasicItem[];
  // Label of the currently loaded topMember dropdown. The current selection is
  // `topMemberSelectionByType[topMemberType]` — see `getSelectedFloorset`.
  topMemberType?: TopMemberDropdownType;
  topMemberSelectionByType?: Partial<Record<TopMemberDropdownType, string>>;
  //for snapshots etc
  extraPivotParamsConfig?: Record<string, ExtraPivotParam>;
  extraPivotParamsOptions?: Record<string, DropdownOption[]>;
  extraPivotParamsSelections: Record<string, string | number | null>;
  activeWorkflowKey: string | null;
  // Companion list selections – persisted globally across views when no favorite is active
  companionSortField: string | null;
  companionSortDirection: SortByDirection;
  companionLevelField: string | null;
  /** Mirrors scope.scope.productMember so blacklist-aware selectors in this slice
   * this is meant to copy/dulpicate the scope member in all cases, and is noly meant to make
   * accessing attributeBlacklist easier in groupBy, favorites, and other sources.
   * This should really only be used for that case and in all other cases state.scope.scope.productMember should be used */
  currentDepartment: string | null;
  // Loaded once at app boot via the loadAttributeBlacklist thunk
  attributeBlacklist?: AttributeBlacklistConfig;
}

export const emptyItem = {
  dataIndex: '',
  text: '',
  dimension: 'product',
};

export const initialGroupBy: GroupBySlice = {
  defaultSelection: 0,
  selection: null,
  selectionDataIndex: null,
  options: [emptyItem],
};

export const initialSortBy: SortBySlice = {
  defaultSelection: 0,
  selection: null,
  direction: 'desc',
  options: [emptyItem],
};

export const initialPareDown = {
  defaultSelections: [],
  selections: [],
  options: [emptyItem],
};

const initialState: SubheaderSlice = {
  search: '',
  altSearch: '',
  groupBy: initialGroupBy,
  sortBy: initialSortBy,
  pareDown: initialPareDown,
  lookBackPeriod: 'YTD',
  flowStatus: [],
  altFlowStatus: [],
  subheaderLoading: false,
  flowStatusConfig: {} as FlowStatus,
  favoritesList: [],
  favorites: [],
  activeFavorite: null,
  possibleBreadcrumbs: null,
  // topMemberDropdown props
  topMemberOptions: [],
  topMemberType: undefined,
  topMemberSelectionByType: {},
  extraPivotParamsConfig: {},
  extraPivotParamsOptions: {},
  extraPivotParamsSelections: {},
  activeWorkflowKey: null,
  companionSortField: null,
  companionSortDirection: 'desc',
  companionLevelField: null,
  currentDepartment: null,
};

export const emptySubheaderState = cloneDeep(initialState);

export function _handleDropdownExtras(
  ddSlice: SubheaderDropdownSlice,
  viewDefn: DropdownConfigViewData,
  currentSelection: TenantConfigViewItem | null,
) {
  ddSlice.options = viewDefn.view;
  if (viewDefn.hideEmptyRow !== true) {
    ddSlice.options.unshift(emptyItem);
  }
  if (viewDefn.default) {
    const defaultSelection = ddSlice.options.findIndex((opt) => opt.dataIndex === viewDefn.default);
    ddSlice.defaultSelection = defaultSelection;
  }
  let newSelection = -1;
  if (!isNil(currentSelection)) {
    newSelection = ddSlice.options.findIndex((opt) => opt.dataIndex === currentSelection.dataIndex);
  }
  if (newSelection >= 0) {
    ddSlice.selection = newSelection;
  } else {
    ddSlice.selection = null;
  }
  if (viewDefn.refreshOnChange) {
    ddSlice.refreshOnChange = viewDefn.refreshOnChange;
  }
  return ddSlice;
}

/**
 * Mirrors the active scope's `productMember` into `currentDepartment` so
 * blacklist-aware logic in this slice has direct access without crossing slices.
 */
function reduceCurrentDepartment(
  state: SubheaderSlice,
  action: ReturnType<typeof receiveScope> | ReturnType<typeof receiveScopeRefreshTrigger>,
): void {
  const nextDepartment = action.payload.scope.productMember ?? null;

  if (state.attributeBlacklist && !isNil(nextDepartment) && state.currentDepartment !== nextDepartment) {
    const previousSelectionDataIndex = state.groupBy.selectionDataIndex;
    const filteredOptions = blacklistByDataIndex(state.groupBy.options, state.attributeBlacklist, nextDepartment);

    let selection: number | null = null;
    let selectionDataIndex: string | null = null;
    if (!isNil(previousSelectionDataIndex)) {
      const idx = filteredOptions.findIndex((opt) => opt.dataIndex === previousSelectionDataIndex);
      if (idx >= 0) {
        selection = idx;
        selectionDataIndex = filteredOptions[idx].dataIndex;
      } else if (filteredOptions.length > 0) {
        selection = 0;
        selectionDataIndex = filteredOptions[0].dataIndex;
      }
    }

    state.groupBy = {
      ...state.groupBy,
      selection,
      selectionDataIndex,
    };
  }

  state.currentDepartment = nextDepartment;
}

/**
 * Returns true when there's an active favorite in either the new `favorites` collection
 * or the deprecated `favoritesList`. Used by view-config extraReducer cases to skip
 * overwriting companion-list defaults while a favorite is driving them.
 */
function hasActiveFavorite(state: SubheaderSlice): boolean {
  return !isNil(state.favorites.find((fav) => fav.active)) || !isNil(state.favoritesList?.find((f) => f.active));
}

/**
 * Check if the old sort selection is in the new options,
 * if it is, keep it and the direction.
 * If it isn't, reset to default and set direction to the default which is always 'desc'
 */
function reconcileCompanionSortField(
  state: SubheaderSlice,
  incomingOptions: { dataIndex: string }[],
  incomingDefault: string | null | undefined,
): void {
  const stillValid =
    !isNil(state.companionSortField) && incomingOptions.some((opt) => opt.dataIndex === state.companionSortField);
  state.companionSortField = stillValid ? state.companionSortField : (incomingDefault ?? null);
  state.companionSortDirection = stillValid ? state.companionSortDirection : 'desc';
}

/**
 * Check if the old level selection is in the new options,
 * if it is, keep it
 * If it isn't, reset to default
 */
function reconcileCompanionLevelField(
  state: SubheaderSlice,
  incomingOptions: { dataIndex: string }[],
  incomingDefault: string | null | undefined,
): void {
  const stillValid =
    !isNil(state.companionLevelField) && incomingOptions.some((opt) => opt.dataIndex === state.companionLevelField);
  state.companionLevelField = stillValid ? state.companionLevelField : (incomingDefault ?? null);
}

/**
 * Processes groupBy properties `hideEmptyRow` and `refreshOnChange` options from config.
 * */
function applyViewDefnToSlice(slice: SubheaderDropdownSlice, viewDefn: DropdownConfigViewData): SubheaderDropdownSlice {
  const { hideEmptyRow, refreshOnChange } = viewDefn;
  const copy = cloneDeep(slice);

  if (hideEmptyRow !== true) {
    copy.options.unshift(emptyItem);
  }

  if (refreshOnChange) {
    copy.refreshOnChange = refreshOnChange;
  }

  return copy;
}

const subheaderSlice = createSlice({
  name: 'subheader',
  initialState: initialState,
  reducers: {
    requestViewDefns: (state, _action: PayloadAction<string[]>) => {
      // this action requires a payload, but it is used in various containers,
      // and isn't meant to go into state
      state.subheaderLoading = true;
    },
    receiveViewDefns: (state, action: PayloadAction<SubheaderDefns>) => {
      const defns = action.payload;
      const { attributeBlacklist, currentDepartment } = state;

      //#region process groupBy viewdefn
      let groupBy: GroupBySlice = cloneDeep(initialGroupBy);
      const groupByDefn = defns.groupBy;

      if (groupByDefn) {
        // TODO: when we decide to use string value instead of index everywhere this condition can be removed
        // this really sucks to have to do this but it is isolated here
        // number => set as value or string => find index of matching dataIndex
        const defaultIndex = isNumber(groupByDefn.default)
          ? groupByDefn.default
          : groupByDefn.view.findIndex((opt) => opt.dataIndex === groupByDefn.default);

        // always try to set the defaultIndex from config, falling back to 0 if unset
        const defaultSelection = defaultIndex >= 0 ? defaultIndex : 0;

        // setup new viewDefn's groupBy options + default selection
        const unprocessedGroupBy: GroupBySlice = {
          ...groupBy,
          options: groupByDefn.view,
          defaultSelection,
        };
        groupBy = applyViewDefnToSlice(unprocessedGroupBy, groupByDefn);
        const previousSelectionDataIndex = state.groupBy.selectionDataIndex;

        if (!isNil(previousSelectionDataIndex)) {
          // selection refers to the blacklisted view of options, so resolve against that
          const optionsForSelection =
            attributeBlacklist && currentDepartment
              ? blacklistByDataIndex(groupBy.options, attributeBlacklist, currentDepartment)
              : groupBy.options;

          const split = previousSelectionDataIndex.split(':');
          const formattedDataIndex = split.length === 1 ? split[0] : split[1];

          const nextGroupByIndex = optionsForSelection.findIndex((gb) => {
            // need to compare raw values instead of formatted values since the format can vary by viewDefn
            // i.e. member:class:name => class or level:subclass:name => subclass
            const gbSplit = gb.dataIndex.split(':');

            // take raw item only, middle item if formatted
            const formattedGbDataIndex = gbSplit.length === 1 ? gbSplit[0] : gbSplit[1];

            return formattedDataIndex === formattedGbDataIndex;
          });

          // validate previous selection is in nextGroupby's options
          const isValid = nextGroupByIndex >= 0;

          groupBy = {
            ...groupBy,
            // only set selection if previous selection is a valid option,
            // otherwise keep null since falling back to default
            selection: isValid ? nextGroupByIndex : null,
            selectionDataIndex: isValid ? optionsForSelection[nextGroupByIndex].dataIndex : null,
          };
        }
      }
      //#endregion

      //#region process sortBy viewDefn
      // TODO: probably want to make sortBy work like groupBy above, with selection validation across views
      let sortBy: SortBySlice = { ...cloneDeep(initialSortBy), direction: state.sortBy.direction || 'desc' };
      if (defns.sortBy) {
        const curSel = get(state.sortBy.options, `[${state.sortBy.selection}]`, null);
        const sortByDefn = defns.sortBy;
        sortBy = {
          ...state.sortBy,
          ..._handleDropdownExtras(sortBy, sortByDefn, curSel),
        };
      }
      //#endregion

      //#region process pareDown
      let pareDown: PareDownSlice = cloneDeep(initialPareDown);
      if (defns.pareDown) {
        const defaultText = (defns.pareDown as any).default || [];
        const pareDownDefn = defns.pareDown;
        const selections = pareDownDefn.view.filter((x) => defaultText.indexOf(x.text) != -1);
        pareDown = {
          ...state.pareDown,
          selections,
          defaultSelections: selections,
          options: pareDownDefn.view,
        };
      }
      //#endregion

      //#region process countLimit
      let countLimitOptions;
      let countLimit;

      if (defns.countLimit) {
        countLimitOptions = defns.countLimit.options;
        // If there is already a count limit selected and it's available in limit options, keep it
        if (state.countLimit && countLimitOptions && countLimitOptions.includes(state.countLimit)) {
          countLimit = state.countLimit;
        } else {
          countLimit = defns.countLimit.default as number;
        }
      }
      //#endregion

      if (!groupByDefn) {
        return {
          ...state,
          sortBy,
          countLimit,
          countLimitOptions,
          countLimitDefault: defns.countLimit ? (defns.countLimit.default as number) : 0,
          subheaderLoading: false,
          groupByDefn: defns.groupBy,
          sortByDefn: defns.sortBy,
          pareDownDefn: defns.pareDown,
          pareDown,
        };
      }

      return {
        ...state,
        groupBy,
        sortBy,
        countLimit,
        countLimitOptions,
        countLimitDefault: defns.countLimit ? (defns.countLimit.default as number) : 0,
        subheaderLoading: false,
        groupByDefn: defns.groupBy,
        sortByDefn: defns.sortBy,
        pareDownDefn: defns.pareDown,
        pareDown,
      };
    },
    updateSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    updateAlternateSearch: (state, action: PayloadAction<string>) => {
      state.altSearch = action.payload;
    },
    updateConfigureSelections: (state, action: PayloadAction<Option[]>) => {
      state.configureSelections = action.payload;
    },
    updateFlowStatus: (state, action: PayloadAction<number[]>) => {
      state.flowStatus = action.payload;
    },
    updateFlowStatusConfig: (state, action: PayloadAction<FlowStatus>) => {
      state.flowStatusConfig = action.payload;
    },
    updateAlternateFlowStatus: (state, action: PayloadAction<number[]>) => {
      state.altFlowStatus = action.payload;
    },
    updateLookBackPeriod: (state, action: PayloadAction<string>) => {
      state.lookBackPeriod = action.payload;
    },
    updateGroupBy: (state, action: PayloadAction<(number | null) | GroupByPayload>) => {
      if (isNil(action.payload)) {
        state.groupBy = {
          ...state.groupBy,
          selection: null,
          selectionDataIndex: null,
        };
        return;
      }

      if (isNumber(action.payload)) {
        // selection refers to the blacklisted view of options, so resolve against that
        const blacklistedOptions =
          state.attributeBlacklist && state.currentDepartment
            ? blacklistByDataIndex(state.groupBy.options, state.attributeBlacklist, state.currentDepartment)
            : state.groupBy.options;
        const idx = action.payload;
        const option = idx >= 0 && idx < blacklistedOptions.length ? blacklistedOptions[idx] : undefined;
        state.groupBy = {
          ...state.groupBy,
          selection: option ? idx : null,
          selectionDataIndex: option?.dataIndex ?? null,
        };
        return;
      }

      const { options, selection } = action.payload;
      state.groupBy = {
        ...state.groupBy,
        options,
        selection,
        selectionDataIndex: selection !== null && selection >= 0 ? options[selection].dataIndex : null,
      };
    },
    updateTopMemberSelection: (state, action: PayloadAction<string>) => {
      if (state.topMemberType) {
        state.topMemberSelectionByType = { ...state.topMemberSelectionByType, [state.topMemberType]: action.payload };
      } else {
        // topMemberType is required so log if its missing
        logWarn('[Subheader] updateTopMemberSelection dispatched with no topMemberType set; selection was not stored');
      }
    },
    receiveTopMemberDropdownData(state, action: PayloadAction<{ options: BasicItem[]; type: TopMemberDropdownType }>) {
      const { options, type } = action.payload;
      // Selection is remembered per dropdown type so navigating between a Floorset view and a Superset view wont overwrite selections
      const previousSelection = state.topMemberSelectionByType?.[type];
      state.topMemberOptions = options;
      state.topMemberType = type;

      // Clear the selection when the response has no dropdown options.
      if (options.length === 0) {
        if (state.topMemberSelectionByType) delete state.topMemberSelectionByType[type];
        return;
      }

      const activeOption = options.find((option: BasicItem) => option.active === true);
      // A breadcrumb link (StyleColor Link on Assortment by Quarter) can carry the
      // originating row's floorset via a `?floorset=` query param, since that view isn't
      // itself floorset-scoped. Prefer it over the backend's own "active" pick when present
      const floorsetFromCrumb = options.find((option: BasicItem) => option.id === floorsetInUrl());

      // Retain the previous selection for this type when it exists in the new record set.
      const previousSelectionIsAvailable = options.some((option: BasicItem) => option.id === previousSelection);
      if (!isNil(previousSelection) && previousSelectionIsAvailable) return;

      // First load for this type, or the previous selection no longer exists: prefer an incoming
      // crumb floorset, then the API's active option, then index 0.
      const resolvedSelection = floorsetFromCrumb?.id ?? activeOption?.id ?? options[0].id;
      state.topMemberSelectionByType = { ...state.topMemberSelectionByType, [type]: resolvedSelection };
    },
    updateSortBy: (state, action: PayloadAction<number | null>) => {
      state.sortBy = {
        ...state.sortBy,
        selection: action.payload,
      };
    },
    updatePareDown: (state, action: PayloadAction<TenantConfigViewItem[]>) => {
      state.pareDown = {
        ...state.pareDown,
        selections: action.payload,
      };
    },
    updateCountLimit: (state, action: PayloadAction<number>) => {
      state.countLimit = action.payload;
    },
    /** @deprecated see updateFavorites instead */
    updateFavoritesList: (state, action: PayloadAction<FavoriteResponseItem[]>) => {
      state.favoritesList = action.payload;

      const activeFavorite = action.payload.find((fav) => fav.active);
      const companionData = activeFavorite?.jsonBlob?.companionData;
      const groupByDataIndex = activeFavorite?.jsonBlob?.groupByDataIndexSelection ?? null;
      // TODO move all this logic to `updateFavorites`
      if (hasActiveFavorite(state) && companionData) {
        const { companionSortField, companionSortDirection, companionLevelField } = companionData;
        if (companionSortField) {
          state.companionSortField = companionSortField;
        }
        if (companionSortDirection) {
          state.companionSortDirection = companionSortDirection;
        }
        if (companionLevelField) {
          state.companionLevelField = companionLevelField;
        }
      }

      if (!isNil(groupByDataIndex)) {
        // selection refers to the blacklisted view of options, so resolve against that
        const blacklistedOptions =
          state.attributeBlacklist && state.currentDepartment
            ? blacklistByDataIndex(state.groupBy.options, state.attributeBlacklist, state.currentDepartment)
            : state.groupBy.options;
        const idx = blacklistedOptions.findIndex((opt) => opt.dataIndex === groupByDataIndex);
        state.groupBy = {
          ...state.groupBy,
          selection: idx >= 0 ? idx : null,
          selectionDataIndex: idx >= 0 ? blacklistedOptions[idx].dataIndex : null,
        };
      }
    },
    updateFavorites: (state, action: PayloadAction<ValidatedFavoriteResponse[]>) => {
      const activeFavorite = action.payload.find((fav) => fav.active);
      state.activeFavorite = isNil(activeFavorite) ? null : activeFavorite.jsonBlob;
      state.favorites = action.payload;
    },
    updateSortByDirection: (state) => {
      if (state.sortBy.direction === 'asc') {
        state.sortBy.direction = 'desc';
      } else {
        state.sortBy.direction = 'asc';
      }
    },
    maybeUpdateSortByDirection: (state, action: PayloadAction<string>) => {
      if (state.sortBy.direction !== action.payload) {
        if (state.sortBy.direction === 'asc') {
          state.sortBy.direction = 'desc';
        } else {
          state.sortBy.direction = 'asc';
        }
      }
    },
    // TODO: The only logic that is needed in this action is the hideEmptyRow logic,
    // everything else (title, boolean values) are not relevant.
    // This should be moved to the receiveViewDefns action handler.
    // For more details see https://s5stratosdev.atlassian.net/browse/INT-1436
    overloadSubheader: (state, action: PayloadAction<OverLoadSubheaderProps>) => {
      const { groupByOptions, ...newProps } = action.payload;

      let options: TenantConfigViewItem[];
      if (!isNil(groupByOptions)) {
        options = groupByOptions?.hideEmptyRow ? [...groupByOptions.view] : [emptyItem, ...groupByOptions.view];
      } else {
        options = [];
      }

      return {
        ...state,
        ...newProps,
        groupBy: {
          ...state.groupBy,
          options,
        },
      };
    },
    receiveError: (_state, action: PayloadAction<string>) => {
      // mild side effects here
      ServiceContainer.loggingService.error(`An error occured in the subheader: ${action.payload}`);
      return initialState;
    },
    setPossibleBreadcrumbs: (state, action: PayloadAction<string[] | null>) => {
      state.possibleBreadcrumbs = action.payload;
    },
    setExtraPivotParamsConfig: (state, action: PayloadAction<Record<string, ExtraPivotParam>>) => {
      state.extraPivotParamsConfig = action.payload;
    },
    setExtraPivotParamsOptions: (state, action: PayloadAction<Record<string, DropdownOption[]>>) => {
      state.extraPivotParamsOptions = {
        ...state.extraPivotParamsOptions,
        ...action.payload,
      };
    },
    clearExtraPivotParams: (state) => {
      state.extraPivotParamsOptions = {};
      state.extraPivotParamsConfig = {};
    },
    setExtraPivotParamsSelection: (state, action: PayloadAction<{ key: string; value: string | number | null }>) => {
      const { key, value } = action.payload;
      state.extraPivotParamsSelections[key] = value;
    },
    updateCompanionSortField: (state, action: PayloadAction<string | null>) => {
      state.companionSortField = action.payload;
    },
    updateCompanionSortDirection: (state, action: PayloadAction<SortByDirection>) => {
      state.companionSortDirection = action.payload;
    },
    updateCompanionLevelByField: (state, action) => {
      state.companionLevelField = action.payload?.dataIndex ?? null;
    },
    cleanUp: () => {
      return initialState;
    },
    setAttributeBlacklist: (state, action: PayloadAction<AttributeBlacklistConfig>) => {
      state.attributeBlacklist = action.payload;
    },
    setActiveWorkflowKey: (state, action: PayloadAction<{ key: string | null; clearsSearch: boolean }>) => {
      const prevKey = state.activeWorkflowKey;
      const nextKey = action.payload.key;

      // Only clear if:
      //  - the new workflow is configured to clear search
      //  - AND its different from the previous workflow
      if (action.payload.clearsSearch && prevKey !== nextKey) {
        state.search = '';
        state.altSearch = '';
      }

      state.activeWorkflowKey = nextKey;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setActivePage, (state) => {
      state.configureSelections = undefined;
    });
    // #region Syncing `currentDepartment` from it's true location in `state.scope.scope.productMember`
    builder.addCase(receiveScope, reduceCurrentDepartment);
    builder.addCase(receiveScopeRefreshTrigger, reduceCurrentDepartment);
    builder.addCase(resetScope, (state) => {
      state.currentDepartment = null;
    });
    builder.addCase(resetOnlyScope, (state) => {
      state.currentDepartment = null;
    });
    //#endregion
    builder.addCase(updateSelectedLevel, (state, action) => {
      state.companionLevelField = action.payload?.dataIndex ?? null;
    });
    builder.addCase(receivePricingOverTimeConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { groupByItems, defaultCompanionLevelField, defaultCompanionSortField, listSort } = action.payload;

      // I don't know why pricing has groupBy in it's config but don't believe it's used
      state.groupBy = { ...state.groupBy, options: groupByItems ?? [] };
      reconcileCompanionLevelField(state, groupByItems ?? [], defaultCompanionLevelField);
      reconcileCompanionSortField(state, listSort?.view ?? [], defaultCompanionSortField);
    });
    builder.addCase(receiveFlowSheetByStyleConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { groupByItems, defaultCompanionLevelField, defaultCompanionSortField, listSort } = action.payload;

      // I don't know why flowsheet has groupBy in it's config but don't believe it's used
      state.groupBy = { ...state.groupBy, options: groupByItems ?? [] };
      reconcileCompanionLevelField(state, groupByItems ?? [], defaultCompanionLevelField);
      reconcileCompanionSortField(state, listSort?.view ?? [], defaultCompanionSortField);
    });
    builder.addCase(receiveWorklistConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { companionView } = action.payload;
      reconcileCompanionSortField(
        state,
        companionView.sortBy?.options ?? [],
        companionView.sortBy?.defaults?.dataIndex,
      );
      reconcileCompanionLevelField(
        state,
        companionView.levelBy?.view ?? [],
        companionView.levelBy?.defaults?.dataIndex,
      );
    });
    builder.addCase(receiveStyleEditConfigs, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const sortBy = action.payload.viewDefn?.companionView?.sortBy;
      reconcileCompanionSortField(state, sortBy?.options ?? [], sortBy?.defaults?.dataIndex);
    });
    builder.addCase(receiveGridViewConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { sortOptions, defaultSort } = parseSortConfig(action.payload.listSort);
      reconcileCompanionSortField(state, sortOptions, defaultSort?.dataIndex);
    });
    builder.addCase(nestedViewReceiveConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { sortOptions, defaultSort } = parseSortConfig(action.payload.listSort);
      reconcileCompanionSortField(state, sortOptions, defaultSort?.dataIndex);
    });
    builder.addCase(listViewReceiveConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { sortOptions, defaultSort } = parseSortConfig(action.payload.listSort);
      reconcileCompanionSortField(state, sortOptions, defaultSort?.dataIndex);
    });
    builder.addCase(receiveStyleByLevelOvertimeConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const sortConfig = action.payload.viewDefn?.companionGrid?.sortConfig;
      reconcileCompanionSortField(state, sortConfig?.options ?? [], sortConfig?.defaultSelection);
    });
  },
});

export const {
  requestViewDefns,
  receiveViewDefns,
  updateSearch,
  updateAlternateSearch,
  updateConfigureSelections,
  updateFlowStatus,
  updateFlowStatusConfig,
  updateAlternateFlowStatus,
  updateLookBackPeriod,
  updateGroupBy,
  updateSortBy,
  updatePareDown,
  updateCountLimit,
  updateFavoritesList,
  updateFavorites,
  updateSortByDirection,
  maybeUpdateSortByDirection,
  overloadSubheader,
  receiveError,
  setPossibleBreadcrumbs,
  setExtraPivotParamsConfig,
  setExtraPivotParamsOptions,
  setExtraPivotParamsSelection,
  clearExtraPivotParams,
  cleanUp,
  updateTopMemberSelection,
  receiveTopMemberDropdownData,
  setActiveWorkflowKey,
  setAttributeBlacklist,
  updateCompanionSortField,
  updateCompanionSortDirection,
  updateCompanionLevelByField,
} = subheaderSlice.actions;

export default subheaderSlice.reducer;
